import React, { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import ConfirmDialog from '@components/Dialogs/ConfirmDialog';
import { CommonKeys } from '@components/EntityDrawers/constants/keys';
import useOwnerOptions from '@components/EntityDrawers/hooks/useOwnerOptions';
import RemovableListItem from '@components/Inputs/Autocomplete/items/RemovableListItem';
import ControlledAutocomplete, {
  ControlledAutocompleteProps,
} from '@components/Inputs/controllers/ControlledAutocomplete';
import { ISelectOption } from '@constants/entities/ui';
import { useProject } from '@context/Project/ProjectProvider';
import { useToast } from '@context/Toast/ToastProvider';
import useProjectId from '@hooks/useProjectId';
import useToggle from '@hooks/useToggle';
import { useRemoveProjectOwnerMutation } from '@store/services/projects';
import { parseErrorResponse } from '@utils/helpers';

type Props = Pick<
  ControlledAutocompleteProps,
  'label' | 'placeholder' | 'readOnly' | 'required'
> & {
  name?: string;
};

const OwnersInput: FC<Props> = ({
  name = CommonKeys.Owners,
  required = true,
  readOnly,
  ...props
}) => {
  const { viewOnly } = useProject();
  const { ownersOptions } = useOwnerOptions(name);
  const [isRemoveOpened, removeControls, ownerOption] =
    useToggle<ISelectOption>();
  const { setValue, getValues } = useFormContext();
  const projectId = useProjectId(true);
  const [removeOwner] = useRemoveProjectOwnerMutation();
  const { showToast } = useToast();

  const handleConfirmRemove = async () => {
    if (!ownerOption?.value) {
      return;
    }

    removeControls.off();

    try {
      await removeOwner({
        projectId,
        owner: ownerOption.value.toString(),
      }).unwrap();
    } catch (error) {
      showToast(parseErrorResponse(error), 'error');
      return;
    }

    setValue(
      name,
      getValues(name).filter(
        (o: ISelectOption) => o.value !== ownerOption.value,
      ),
    );
  };

  return (
    <>
      <ConfirmDialog
        open={isRemoveOpened}
        onClose={removeControls.off}
        title="Delete owner"
        text={`Are you sure you want to remove ${
          ownerOption?.label ?? ''
        } from the list of owners? This action will affect all related entities and permanently delete the owner from the system.`}
        onConfirm={handleConfirmRemove}
      />

      <ControlledAutocomplete
        name={name}
        required={required}
        readOnly={viewOnly || readOnly}
        options={ownersOptions}
        sortValue
        itemComponent={RemovableListItem}
        itemProps={(option) => ({
          onRemove: () => removeControls.on(option),
        })}
        multiple
        freeSolo
        normalizeFreeSoloToOption
        canFlip
        {...props}
      />
    </>
  );
};

export default OwnersInput;
